/**
 * Shared pose mirroring utilities.
 *
 * This is the single canonical source for left/right landmark swapping.
 * Both goalOverlayHelpers.ts (index-map lookup) and usePoseExtraction.ts
 * (array mirroring) derive their behaviour from the LANDMARK_SWAP_PAIRS
 * constant defined here.
 */

// ---------------------------------------------------------------------------
// Canonical swap-pair list
// ---------------------------------------------------------------------------

/**
 * MediaPipe landmark index pairs for corresponding left/right body parts.
 * Each tuple is [leftIndex, rightIndex].
 * Used both for building MIRROR_INDEX_MAP and for swapping landmark arrays.
 */
export const LANDMARK_SWAP_PAIRS: [number, number][] = [
  // Face
  [1, 4],   // left_eye_inner  <-> right_eye_inner
  [2, 5],   // left_eye        <-> right_eye
  [3, 6],   // left_eye_outer  <-> right_eye_outer
  [7, 8],   // left_ear        <-> right_ear
  [9, 10],  // mouth_left      <-> mouth_right
  // Upper body
  [11, 12], // left_shoulder   <-> right_shoulder
  [13, 14], // left_elbow      <-> right_elbow
  [15, 16], // left_wrist      <-> right_wrist
  [17, 18], // left_pinky      <-> right_pinky
  [19, 20], // left_index      <-> right_index
  [21, 22], // left_thumb      <-> right_thumb
  // Lower body
  [23, 24], // left_hip        <-> right_hip
  [25, 26], // left_knee       <-> right_knee
  [27, 28], // left_ankle      <-> right_ankle
  [29, 30], // left_heel       <-> right_heel
  [31, 32], // left_foot_index <-> right_foot_index
];

// ---------------------------------------------------------------------------
// MIRROR_INDEX_MAP derived from the canonical pair list
// ---------------------------------------------------------------------------

/**
 * Maps each landmark index to its opposite-side counterpart.
 * Derived from LANDMARK_SWAP_PAIRS so both data structures are always in sync.
 */
export const MIRROR_INDEX_MAP: Record<number, number> = LANDMARK_SWAP_PAIRS.reduce<
  Record<number, number>
>((acc, [l, r]) => {
  acc[l] = r;
  acc[r] = l;
  return acc;
}, {});

// ---------------------------------------------------------------------------
// mirrorLandmarks
// ---------------------------------------------------------------------------

/**
 * Mirror a landmarks array horizontally for opposite-handed athlete comparison.
 *
 * - Flips x-coordinates (normalized 0-1 space).
 * - Swaps each left/right landmark pair so that index semantics are preserved
 *   from the caller's perspective (e.g. index 11 = left_shoulder after mirror
 *   contains what was previously the right shoulder).
 *
 * Returns undefined when landmarks is empty or undefined.
 */
export function mirrorLandmarks(
  landmarks: Array<{ x: number; y: number; visibility?: number }> | undefined,
): Array<{ x: number; y: number; visibility?: number }> | undefined {
  if (!landmarks || landmarks.length === 0) {
    return undefined;
  }

  // Flip x-coordinates on a fresh copy
  const mirrored = landmarks.map((lm) => ({ ...lm, x: 1.0 - lm.x }));

  // Swap left/right pairs
  for (const [leftIdx, rightIdx] of LANDMARK_SWAP_PAIRS) {
    if (leftIdx < mirrored.length && rightIdx < mirrored.length) {
      const temp = mirrored[leftIdx];
      mirrored[leftIdx] = mirrored[rightIdx];
      mirrored[rightIdx] = temp;
    }
  }

  return mirrored;
}
